import { ref, watch } from 'vue';
import { isFunction } from '@/helpers/misc/types';
import isEquals from './isEquals';

/**
 * This function determines whether a value keeps its originality or not
 * @param {object,function} originalValue Unmodified value
 * @param {object,string,number} lazyValue Possible value to changed
 * @returns {{isDirty, reset}}
 * @example{
 *   const originalValue = () => {id:5, title:"anyTitle", description:"anyDescription"};
 *
 *   const possibleValueToChanged = {id:5, title:"anyTitle"};
 *
 *   const isDirty = useDirty(originalValue, possibleValueToChanged);
 *
 *   return {
 *     isDirty = false  // false because the original value has changed.
 *     reset            // the function that returns isDirt to the default value
 *   }
 * }
 */
export const useDirty = (originalValue, lazyValue) => {
  const isDirty = ref(false);

  watch(
    lazyValue,
    newValue => {
      isDirty.value = !isEquals(
        isFunction(originalValue) ? originalValue() : originalValue,
        newValue,
      );
    },
    { deep: true },
  );

  const reset = () => {
    isDirty.value = false;
  };

  return {
    isDirty,
    reset,
  };
};
